import { CheckIntegrationOvermind } from 'domain/usecases/eligibility/check-integration-overmind'
import { CreateConsultEligibility } from 'domain/usecases/eligibility/create-consult-eligibility'
import { CreateEligibility } from 'domain/usecases/eligibility/create-eligibility'
import { EligibilityCheck } from 'domain/usecases/eligibility/eligibility-check'
import { FindConsultsEligibility } from 'domain/usecases/eligibility/find-consults-eligibility'
import { UpdateEligibilityDocument } from 'domain/usecases/eligibility/update-eligibility-document'
import { RepositoryErrors } from 'repository/errors/repository-errors'
import { checkEligibility } from 'repository/graphql/queries'
import handleGraphQLError from 'repository/graphql/utils/handle-error'
import { makeGraphQLVariable } from 'repository/graphql/utils/make-variables'
import { EligibilityRepository as IEligibilityRepository } from 'repository/interfaces/eligibility-repository'
import { ApiStatusCode } from 'service/protocols/api/api-client'
import { IApiRepository } from 'service/protocols/api/api-repository'

type Eligibility = {
  eligible: boolean
  card: string
}

export class EligibilityRepository implements IEligibilityRepository {
  constructor(private readonly apiRepository: IApiRepository) {}

  async eligibilityCheck(
    data: EligibilityCheck.Data,
    params: EligibilityCheck.Params
  ): Promise<EligibilityCheck.Model> {
    const apiRepository = this.apiRepository as IApiRepository<Eligibility>

    const query = checkEligibility(params)

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: {
          ...makeGraphQLVariable(data, 'data')
        }
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return httpResponse.body as Eligibility
    }
  }

  async checkIntegrationOvermind(
    surgical_order_id: number
  ): Promise<CheckIntegrationOvermind.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CheckIntegrationOvermind.Model>

    const httpResponse: any = await apiRepository.get(
      {
        url: `/health-insurance/check-integration-overmind?surgical_order_id=${surgical_order_id}`
      },
      false
    )

    return {
      data: httpResponse.body
    }
  }

  async findConsultsEligibility(
    surgical_order_id: number
  ): Promise<FindConsultsEligibility.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<FindConsultsEligibility.Model>

    const httpResponse: any = await apiRepository.get(
      {
        url: `/health-insurance/find-consults-eligibility?surgical_order_id=${surgical_order_id}`
      },
      false
    )

    return {
      data: httpResponse.body
    }
  }

  async createConsultEligibility(
    params: CreateConsultEligibility.Params
  ): Promise<CreateConsultEligibility.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CreateConsultEligibility.Model>

    const httpResponse = await apiRepository.post({
      url: `/health-insurance/consult-eligibility`,
      body: params
    })

    return httpResponse.body as unknown as CreateConsultEligibility.Model
  }

  async createEligibility(
    params: CreateEligibility.Params
  ): Promise<CreateEligibility.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CreateEligibility.Model>

    const httpResponse = await apiRepository.post({
      url: `/health-insurance/create-eligibility`,
      body: params
    })

    return httpResponse.body as unknown as CreateEligibility.Model
  }

  async updateDocumentStatus(
    params: UpdateEligibilityDocument.Params
  ): Promise<UpdateEligibilityDocument.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<UpdateEligibilityDocument.Model>

    const httpResponse = await apiRepository.post({
      url: `/health-insurance/consults/validation-document`,
      body: {
        health_insurance_status_id: params.health_insurance_status_id,
        document_status: params.document_status
      }
    })

    return httpResponse.body as unknown as UpdateEligibilityDocument.Model
  }
}
